import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Plus, Trash2, Wand2, Loader2, Users, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { engageApi } from "@/lib/engageApi";
import { leadsApi, segmentsApi } from "@/lib/leadsApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar } from "@/components/find/atoms";

const emptyStep = () => ({
    step_id: `step_${Math.random().toString(36).slice(2, 10)}`,
    day_offset: 0,
    subject: "",
    body: "",
    variant: "A",
});

export default function CampaignWizard() {
    const { t } = useI18n();
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [name, setName] = useState("");
    const [senderName, setSenderName] = useState(user?.name || "");
    const [valueProp, setValueProp] = useState("");
    const [source, setSource] = useState("segment"); // segment | manual
    const [segmentId, setSegmentId] = useState("");
    const [segments, setSegments] = useState([]);
    const [segmentLeadIds, setSegmentLeadIds] = useState([]);
    const [manualLeads, setManualLeads] = useState([]);
    const [steps, setSteps] = useState([emptyStep()]);
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        segmentsApi.list().then(setSegments).catch(() => {});
    }, []);

    useEffect(() => {
        if (source === "segment" && segmentId) {
            segmentsApi.leads(segmentId, { page: 1, page_size: 100 })
                .then((d) => setSegmentLeadIds(d.items.map((l) => l.lead_id)))
                .catch(() => setSegmentLeadIds([]));
        } else {
            setSegmentLeadIds([]);
        }
    }, [source, segmentId]);

    const leadIds = source === "segment" ? segmentLeadIds : manualLeads.map((l) => l.lead_id);
    const audienceCount = leadIds.length;
    const firstLead = source === "manual" ? manualLeads[0] : null;

    function nextStep() {
        if (step === 0) {
            if (!name.trim()) return toast.error(t.engage.wizard.needName);
            if (!senderName.trim()) return toast.error(t.engage.wizard.needSender);
            if (audienceCount === 0) return toast.error(t.engage.wizard.needAudience);
        }
        if (step === 1) {
            if (steps.length === 0) return toast.error(t.engage.wizard.needStep);
            for (const s of steps) {
                if (!s.subject.trim() || !s.body.trim()) return toast.error(t.engage.wizard.needStepContent);
            }
        }
        setStep(Math.min(2, step + 1));
    }

    async function saveDraft(launch = false) {
        setLaunching(true);
        try {
            const created = await engageApi.campaigns.create({
                name: name.trim(),
                lead_ids: leadIds,
                segment_id: source === "segment" ? segmentId : null,
                sender_name: senderName.trim(),
                value_prop: valueProp.trim(),
                steps,
            });
            if (launch) {
                const res = await engageApi.campaigns.send(created.campaign_id);
                toast.success(t.engage.campaigns.sentBanner(res.scheduled, res.simulated));
            } else {
                toast.success("Draft saved");
            }
            navigate(`/app/engage/campaigns/${created.campaign_id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setLaunching(false);
        }
    }

    async function generateAIEmail(idx) {
        const leadId = source === "manual" ? firstLead?.lead_id : segmentLeadIds[0];
        if (!leadId) {
            return toast.info(t.engage.wizard.pickLeadForAI);
        }
        try {
            const res = await engageApi.emails.generate(leadId, senderName || user?.name || "Alex", valueProp);
            const next = [...steps];
            next[idx] = { ...next[idx], subject: res.subject, body: res.body };
            setSteps(next);
            refresh();
            toast.success("AI email generated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <Link to="/app/engage/campaigns" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition" data-testid="wizard-back">
                <ArrowLeft size={14} /> {t.engage.detail.back}
            </Link>

            <div className="mt-4 flex items-center gap-3 flex-wrap">
                <h1 className="font-display font-black text-3xl tracking-tighter">{t.engage.wizard.title}</h1>
                <div className="flex-1" />
                <Stepper step={step} t={t} />
            </div>

            <div className="mt-8">
                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <motion.div key="s0" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-4">
                                <Field label={t.engage.wizard.nameLabel} value={name} onChange={setName} placeholder={t.engage.wizard.namePh} testId="wizard-name" />
                                <Field label={t.engage.wizard.senderLabel} value={senderName} onChange={setSenderName} placeholder={t.engage.wizard.senderPh} testId="wizard-sender" />
                                <Field label={t.engage.wizard.valueLabel} value={valueProp} onChange={setValueProp} placeholder={t.engage.wizard.valuePh} testId="wizard-value" textarea rows={3} />
                            </div>
                            <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{t.engage.wizard.audienceSource}</div>
                                <div className="space-y-2 mb-4">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="radio" checked={source === "segment"} onChange={() => setSource("segment")} data-testid="wizard-source-segment" className="accent-brand-indigo" />
                                        <span>{t.engage.wizard.sourceSegment}</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="radio" checked={source === "manual"} onChange={() => setSource("manual")} data-testid="wizard-source-manual" className="accent-brand-indigo" />
                                        <span>{t.engage.wizard.sourceManual}</span>
                                    </label>
                                </div>
                                {source === "segment" && (
                                    segments.length === 0 ? (
                                        <div className="text-xs text-zinc-500">{t.engage.wizard.noSegments}</div>
                                    ) : (
                                        <Select value={segmentId} onValueChange={setSegmentId}>
                                            <SelectTrigger className="bg-brand-void border-white/10 h-10 w-full" data-testid="wizard-segment-select">
                                                <SelectValue placeholder={t.engage.wizard.selectSegment} />
                                            </SelectTrigger>
                                            <SelectContent className="bg-brand-surface border-white/10 text-white">
                                                {segments.map((s) => (
                                                    <SelectItem key={s.segment_id} value={s.segment_id}>{s.name} ({s.lead_count})</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )
                                )}
                                {source === "manual" && (
                                    <ManualLeadPicker manualLeads={manualLeads} setManualLeads={setManualLeads} />
                                )}
                                <div className="mt-4 pt-4 border-t border-white/8 flex items-center gap-2 text-sm text-white">
                                    <Users size={14} className="text-brand-cyan" />
                                    <span data-testid="wizard-audience-count">{t.engage.wizard.leadsSelected(audienceCount)}</span>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 1 && (
                        <motion.div key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                            <p className="text-sm text-zinc-500 max-w-2xl">{t.engage.wizard.sequenceIntro}</p>
                            {steps.map((s, i) => (
                                <div key={s.step_id} className="rounded-2xl p-5 bg-brand-surface border border-white/8" data-testid={`wizard-step-${i}`}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-indigo to-brand-violet text-white text-sm font-bold flex items-center justify-center">
                                            {i + 1}
                                        </div>
                                        <div className="text-sm text-zinc-400 font-mono">{t.engage.wizard.dayOffset(s.day_offset)}</div>
                                        <div className="flex-1" />
                                        {i > 0 && (
                                            <button type="button" onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-rose-300" data-testid={`wizard-step-remove-${i}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                        {i > 0 && (
                                            <div>
                                                <label className="text-xs text-zinc-500 mb-1 block">{t.engage.wizard.stepDayLabel}</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={s.day_offset}
                                                    onChange={(e) => updateStep(steps, setSteps, i, { day_offset: parseInt(e.target.value, 10) || 0 })}
                                                    data-testid={`wizard-step-day-${i}`}
                                                    className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                                                />
                                            </div>
                                        )}
                                        <div className={i === 0 ? "md:col-span-3" : "md:col-span-2"}>
                                            <label className="text-xs text-zinc-500 mb-1 block">{t.engage.wizard.stepSubject}</label>
                                            <input
                                                value={s.subject}
                                                onChange={(e) => updateStep(steps, setSteps, i, { subject: e.target.value })}
                                                data-testid={`wizard-step-subject-${i}`}
                                                className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-zinc-500 mb-1 block">{t.engage.wizard.stepVariant}</label>
                                            <Select value={s.variant} onValueChange={(v) => updateStep(steps, setSteps, i, { variant: v })}>
                                                <SelectTrigger className="bg-brand-void border-white/10 h-10" data-testid={`wizard-step-variant-${i}`}><SelectValue /></SelectTrigger>
                                                <SelectContent className="bg-brand-surface border-white/10 text-white">
                                                    <SelectItem value="A">A</SelectItem>
                                                    <SelectItem value="B">B</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <label className="text-xs text-zinc-500 mb-1 block">{t.engage.wizard.stepBody}</label>
                                        <textarea
                                            value={s.body}
                                            onChange={(e) => updateStep(steps, setSteps, i, { body: e.target.value })}
                                            rows={6}
                                            data-testid={`wizard-step-body-${i}`}
                                            className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo resize-y font-mono"
                                        />
                                    </div>
                                    <div className="mt-3 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => generateAIEmail(i)}
                                            disabled={(user?.credits ?? 0) < 1}
                                            data-testid={`wizard-step-ai-${i}`}
                                            className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/10 text-brand-cyan inline-flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <Wand2 size={12} /> {t.engage.wizard.generateAI}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setSteps([...steps, { ...emptyStep(), day_offset: 3 }])}
                                data-testid="wizard-add-step"
                                className="w-full py-3 rounded-2xl border border-dashed border-white/15 hover:border-brand-indigo hover:bg-brand-indigo/5 text-zinc-300 text-sm inline-flex items-center justify-center gap-2"
                            >
                                <Plus size={14} /> {t.engage.wizard.addStep}
                            </button>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-4">
                                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                                    <h3 className="font-display font-bold text-xl mb-4">{t.engage.wizard.reviewTitle}</h3>
                                    <SummaryRow label="Name" value={name} />
                                    <SummaryRow label="Sender" value={senderName} />
                                    <SummaryRow label={t.engage.wizard.reviewValue} value={valueProp || "—"} />
                                    <SummaryRow label={t.engage.wizard.reviewAudience} value={t.engage.wizard.leadsSelected(audienceCount)} highlight />
                                </div>
                                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                                    <div className="text-sm font-semibold mb-3">{t.engage.wizard.reviewSequence}</div>
                                    <ol className="space-y-3">
                                        {steps.map((s, i) => (
                                            <li key={s.step_id} className="flex gap-3">
                                                <div className="w-6 h-6 rounded-full bg-brand-indigo/20 text-brand-cyan text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white truncate">{s.subject || <span className="text-zinc-500 italic">no subject</span>}</div>
                                                    <div className="text-xs text-zinc-500 mt-0.5 font-mono">{t.engage.wizard.dayOffset(s.day_offset)} · variant {s.variant}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <button
                                    type="button"
                                    onClick={() => saveDraft(true)}
                                    disabled={launching}
                                    data-testid="wizard-launch"
                                    className="w-full btn-cta text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    {launching ? <><Loader2 size={14} className="animate-spin" /> {t.engage.wizard.launching}</> : <><Send size={14} /> {t.engage.wizard.launch}</>}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => saveDraft(false)}
                                    disabled={launching}
                                    data-testid="wizard-save-draft"
                                    className="w-full text-white font-medium py-3 rounded-lg border border-white/12 hover:border-white/25 hover:bg-white/5 transition disabled:opacity-60"
                                >
                                    {t.engage.wizard.saveDraft}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => step === 0 ? navigate("/app/engage/campaigns") : setStep(step - 1)}
                    data-testid="wizard-prev"
                    className="text-white font-medium px-4 py-2 rounded-full border border-white/12 hover:border-white/25 hover:bg-white/5 transition inline-flex items-center gap-2"
                >
                    <ArrowLeft size={14} /> {step === 0 ? t.engage.wizard.cancel : t.engage.wizard.back}
                </button>
                {step < 2 && (
                    <button
                        type="button"
                        onClick={nextStep}
                        data-testid="wizard-next"
                        className="btn-cta text-white font-semibold px-5 py-2 rounded-full inline-flex items-center gap-2"
                    >
                        {t.engage.wizard.next} <ArrowRight size={14} />
                    </button>
                )}
            </div>
        </div>
    );
}

function updateStep(steps, setSteps, i, patch) {
    setSteps(steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
}

function Stepper({ step, t }) {
    const labels = [t.engage.wizard.steps.audience, t.engage.wizard.steps.sequence, t.engage.wizard.steps.review];
    return (
        <div className="flex items-center gap-2">
            {labels.map((label, i) => (
                <React.Fragment key={label}>
                    <div className={`flex items-center gap-2 ${i <= step ? "text-white" : "text-zinc-600"}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            i < step ? "bg-brand-cyan text-brand-void" : i === step ? "bg-brand-indigo text-white" : "bg-white/8 text-zinc-500"
                        }`}>
                            {i < step ? <Check size={12} /> : i + 1}
                        </div>
                        <span className="text-xs uppercase tracking-widest font-bold hidden sm:inline">{label}</span>
                    </div>
                    {i < 2 && <div className={`h-px w-6 sm:w-10 ${i < step ? "bg-brand-cyan" : "bg-white/10"}`} />}
                </React.Fragment>
            ))}
        </div>
    );
}

function Field({ label, value, onChange, placeholder, testId, textarea, rows = 3 }) {
    return (
        <div>
            <label className="text-xs font-medium text-zinc-400">{label}</label>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={rows}
                    data-testid={testId}
                    className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg bg-brand-surface border border-white/10 focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo outline-none text-sm text-white placeholder:text-zinc-600 resize-y"
                />
            ) : (
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testId}
                    className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg bg-brand-surface border border-white/10 focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo outline-none text-sm text-white placeholder:text-zinc-600"
                />
            )}
        </div>
    );
}

function SummaryRow({ label, value, highlight }) {
    return (
        <div className="flex items-baseline justify-between py-2 border-b border-white/5 last:border-b-0">
            <div className="text-xs text-zinc-500">{label}</div>
            <div className={`text-sm text-right ml-4 min-w-0 truncate ${highlight ? "text-brand-cyan font-mono" : "text-white"}`}>{value}</div>
        </div>
    );
}

function ManualLeadPicker({ manualLeads, setManualLeads }) {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!search.trim()) { setResults([]); return; }
        setLoading(true);
        const t = setTimeout(() => {
            leadsApi.list({ search: search.trim(), page_size: 10 })
                .then((d) => setResults(d.items))
                .catch(() => setResults([]))
                .finally(() => setLoading(false));
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    function add(lead) {
        if (manualLeads.find((l) => l.lead_id === lead.lead_id)) return;
        setManualLeads([...manualLeads, lead]);
        setSearch("");
        setResults([]);
    }

    return (
        <div>
            <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search a lead by name / company…"
                data-testid="wizard-manual-search"
                className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
            />
            {loading && <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Searching…</div>}
            {results.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-brand-void">
                    {results.map((r) => (
                        <button
                            key={r.lead_id}
                            type="button"
                            onClick={() => add(r)}
                            data-testid={`wizard-manual-result-${r.lead_id}`}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-white/[0.05] text-sm"
                        >
                            <Avatar seed={r.avatar_seed} size={22} />
                            <span className="truncate flex-1">{r.full_name} · <span className="text-zinc-500">{r.company_name}</span></span>
                            <Plus size={12} className="text-brand-cyan" />
                        </button>
                    ))}
                </div>
            )}
            {manualLeads.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {manualLeads.map((l) => (
                        <span
                            key={l.lead_id}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-brand-indigo/15 border border-brand-indigo/30 text-xs text-white"
                            data-testid={`wizard-manual-chip-${l.lead_id}`}
                        >
                            {l.full_name}
                            <button type="button" onClick={() => setManualLeads(manualLeads.filter((x) => x.lead_id !== l.lead_id))} className="text-zinc-400 hover:text-white">×</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
